<template>
  <main
    class="landing-erp"
    :class="{ 'light-mode': !isDark, 'ltr-mode': selectedLanguage === 'en' }"
    :dir="selectedLanguage === 'en' ? 'ltr' : 'rtl'"
  >
    <div class="ambient ambient-one" aria-hidden="true"></div>
    <div class="ambient ambient-two" aria-hidden="true"></div>
    <div class="ambient ambient-three" aria-hidden="true"></div>

    <NavbarStyled v-model:is-dark="isDark" v-model:selected-language="selectedLanguage" />

    <section id="top" class="hero landing-container">
      <div class="hero-orbit" aria-hidden="true">
        <div class="orbit-ring ring-one"></div>
        <div class="orbit-ring ring-two"></div>
        <div class="core"><span></span></div>
        <div class="orbit-dot dot-a"></div>
        <div class="orbit-dot dot-b"></div>
        <div class="orbit-dot dot-c"></div>
        <div class="orbit-label label-a">ERP / AI</div>
        <div class="orbit-label label-b">DATA FLOW</div>
      </div>

      <div class="hero-copy-wrap">
        <div class="eyebrow hero-eyebrow"><span class="pulse-dot"></span>{{ activeCopy.hero.eyebrow }}</div>
        <h1>
          {{ activeCopy.hero.title }}
        </h1>
        <p class="hero-copy">{{ activeCopy.hero.text }}</p>
        <div class="hero-actions">
          <a class="button button-primary" href="#contact" @click.prevent="scrollToSection('contact')">
            {{ activeCopy.hero.primary }}
          </a>
          <a class="button button-ghost" href="#about" @click.prevent="scrollToSection('about')">
            {{ activeCopy.hero.secondary }} <span>↓</span>
          </a>
        <div class="auth-buttons">
  <a class="auth-btn auth-btn-login" href="/login">
    {{ activeCopy.auth.login }}
  </a>
  <a class="auth-btn auth-btn-register" href="/register">
    {{ activeCopy.auth.register }}
  </a>
</div>
        </div>
      </div>
    </section>

    <section class="metrics-strip">
      <div class="landing-container metrics-grid">
        <div v-for="stat in activeCopy.stats" :key="stat.label" class="metric">
          <strong>{{ stat.value }}</strong>
          <span>{{ stat.label }}</span>
        </div>
      </div>
    </section>

    <section id="about" class="section landing-container about">
      <div class="section-heading about-copy">
        <p class="eyebrow">{{ activeCopy.about.eyebrow }}</p>
        <h2>{{ activeCopy.about.title }}</h2>
        <p>{{ activeCopy.about.text }}</p>
        <ul>
          <li v-for="item in activeCopy.about.items" :key="item">
            <span class="check-mark" aria-hidden="true">✓</span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <div class="about-visual" aria-label="ERP analytics preview">
        <div class="visual-grid"></div>
        <div class="dashboard-card">
          <span class="mini-label">ALMAS AI / OVERVIEW</span>
          <div class="chart">
            <i v-for="(bar, index) in chartBars" :key="index" :style="{ height: bar + '%' }"></i>
          </div>
          <div class="dashboard-line">
            <b></b>
            <span>{{ selectedLanguage === 'en' ? 'Organization performance' : selectedLanguage === 'ar' ? 'أداء المؤسسة' : 'عملکرد سازمان' }}</span>
            <strong>+۲۴.۸٪</strong>
          </div>
        </div>
      </div>
    </section>

    <section id="modules" class="section landing-container modules-section">
      <div class="section-top section-top-centered">
        <div>
          <h2>{{ activeCopy.modules.title }}</h2>
          <p class="section-subtitle">{{ activeCopy.modules.text }}</p>
        </div>
      </div>

      <div class="module-grid">
        <article v-for="(module, index) in activeModules" :key="module.title" class="module-card">
          <img class="module-card-image" :src="module.image" alt="" loading="lazy" decoding="async" aria-hidden="true" />
          <div class="module-card-content">
            <span class="module-number">{{ formatNumber(index + 1) }}</span>
            <h3>{{ module.title }}</h3>
            <p>{{ module.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="section landing-container timeline-section">
      <div class="section-heading centered">
        <h2>{{ activeCopy.timeline.title }}</h2>
      </div>

      <div class="erp-timeline">
        <div v-for="(item, index) in activeTimeline" :key="item.year" class="erp-timeline-item">
          <div class="erp-timeline-dot">{{ formatNumber(index + 1) }}</div>
          <strong>{{ item.year }}</strong>
          <p>{{ item.text }}</p>
        </div>
      </div>
    </section>

    <section id="ai" class="section ai-section">
      <div class="landing-container">
        <div class="section-top section-top-centered">
          <div>
            <h2>{{ activeCopy.ai.title }}</h2>
            <p class="section-subtitle">{{ activeCopy.ai.text }}</p>
          </div>
        </div>

        <div class="ai-grid">
          <article v-for="feature in activeAi" :key="feature.title" class="ai-card">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section landing-container journey">
      <div class="section-top section-top-centered centered ">
        <h2>{{ activeCopy.journey.title }}</h2>
        <p class="section-subtitle">{{ activeCopy.journey.text }}</p>
      </div>

      <div class="journey-grid">
        <div v-for="step in activeJourney" :key="step" class="journey-step">
          <div>
            <h3>{{ step }}</h3>
          </div>
        </div>
      </div>
    </section>

    <div class="footer-zone">
      <section id="contact" class="contact-section landing-container">
        <div class="contact-copy">
          <h2>{{ activeCopy.contact.title }}</h2>
          <p>{{ activeCopy.contact.text }}</p>

          <div class="contact-details">
            <div><span>{{ activeCopy.contact.phoneLabel }}</span><a href="tel:02188888888">{{ activeCopy.contact.phone }}</a></div>
            <div><span>{{ activeCopy.contact.emailLabel }}</span><a href="mailto:info@khaneh-software.com">{{ activeCopy.contact.email }}</a></div>
            <div><span>{{ activeCopy.contact.hoursLabel }}</span><b>{{ activeCopy.contact.hours }}</b></div>
          </div>

          <div class="contact-links">
            <div class="contact-social-links">
              <h3>{{ t('landing.footer.social.title') }}</h3>
              <div class="social-icons">
                <a href="#" class="social-icon" :title="t('landing.footer.social.instagram')" aria-label="Instagram">
                  <img :src="instagramIcon" alt="" />
                </a>
                <a href="#" class="social-icon" :title="t('landing.footer.social.telegram')" aria-label="Telegram">
                  <img :src="telegramIcon" alt="" />
                </a>
                <a href="#" class="social-icon" :title="t('landing.footer.social.linkedin')" aria-label="LinkedIn">
                  <img :src="linkedinIcon" alt="" />
                </a>
                <a href="#" class="social-icon" :title="t('landing.footer.social.twitter')" aria-label="Twitter">
                  <img :src="twitterIcon" alt="" />
                </a>
              </div>
            </div>

          </div>
        </div>

        <div class="contact-form-card">
          <form class="contact-form" @submit.prevent="submitForm">
            <label class="form-field">
              <span>{{ t('landing.contact.form.name') }}</span>
              <input
                v-model="form.name"
                type="text"
                autocomplete="name"
                required
                :placeholder="t('landing.contact.form.name')"
              />
            </label>

            <label class="form-field">
              <span>{{ t('landing.contact.form.phone') }}</span>
              <input
                v-model="form.phone"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                dir="ltr"
                required
                :placeholder="t('landing.contact.form.phone')"
              />
            </label>

            <label class="form-field">
              <span>{{ t('landing.contact.form.email') }}</span>
              <input
                v-model="form.email"
                type="email"
                autocomplete="email"
                dir="ltr"
                required
                :placeholder="t('landing.contact.form.email')"
              />
            </label>

            <label class="form-field">
              <span>{{ t('landing.contact.form.type') }}</span>
              <select v-model="form.businessType" required>
                <option value="" disabled>{{ t('landing.contact.form.type') }}</option>
                <option value="store">{{ t('landing.contact.form.store') }}</option>
                <option value="company">{{ t('landing.contact.form.company') }}</option>
                <option value="factory">{{ t('landing.contact.form.factory') }}</option>
                <option value="other">{{ t('landing.contact.form.other') }}</option>
              </select>
            </label>

            <label class="form-field form-field-wide">
              <span>{{ t('landing.contact.form.message') }}</span>
              <textarea
                v-model="form.message"
                rows="4"
                required
                :placeholder="t('landing.contact.form.message')"
              ></textarea>
            </label>

            <button class="button button-primary form-submit" type="submit" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="form-spinner" aria-hidden="true"></span>
              {{ isSubmitting ? t('landing.contact.form.sending') : activeCopy.contact.button }}
            </button>

            <p
              v-if="formStatus.message"
              class="form-status"
              :class="`form-status-${formStatus.type}`"
              role="status"
              aria-live="polite"
            >
              {{ formStatus.message }}
            </p>
          </form>

          <div class="social-proof">
            <div class="proof-orb" aria-hidden="true">✦</div>
            <h3>{{ activeCopy.contact.proof }}</h3>
          </div>
        </div>
      </section>

      <footer class="footer landing-container">
        <div class="footer-brand">
          <img class="footer-logo" :src="logoImage" :alt="t('brand')" />
        </div>
        <p>{{ activeCopy.footer.tagline }}</p>
        <div class="footer-links">
          <a href="#top" @click.prevent="scrollToSection('top')">{{ activeCopy.footer.links[0] }}</a>
          <a href="#modules" @click.prevent="scrollToSection('modules')">{{ activeCopy.footer.links[1] }}</a>
          <a href="#ai" @click.prevent="scrollToSection('ai')">{{ activeCopy.footer.links[2] }}</a>
          <a href="#contact" @click.prevent="scrollToSection('contact')">{{ activeCopy.footer.links[3] }}</a>
        </div>
        <small>{{ activeCopy.footer.rights }}</small>
      </footer>
    </div>
  </main>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import NavbarStyled from '@/components/navbar_styled.vue'
import { contactService } from '@/services/api'

import warehouseImage from '@/assets/mc-01.jpg'
import salesImage from '@/assets/mc-2.jpg'
import procurementImage from '@/assets/mc-03.jpg'
import financeImage from '@/assets/mc-4.jpg'
import crmImage from '@/assets/mc-5.jpg'
import qualityImage from '@/assets/mc-06.jpg'
import logoImage from '@/assets/Logo4.svg'
import instagramIcon from '@/assets/SocialMedia/instagram_icon.svg'
import linkedinIcon from '@/assets/SocialMedia/linkedin_icon.svg'
import telegramIcon from '@/assets/SocialMedia/telegram_icon.svg'
import twitterIcon from '@/assets/SocialMedia/twitter_icon.svg'

const languages = [
  { value: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
]

const { t, locale } = useI18n()
const selectedLanguage = ref(locale.value || 'fa')
const isDark = ref(true)
const isSubmitting = ref(false)
const formStatus = ref({ type: '', message: '' })
const form = ref({
  name: '',
  phone: '',
  email: '',
  businessType: '',
  message: '',
})

const statsContent = [
  { value: '+500', label: 'landing.erp.stats.companies' },
  { value: '+20', label: 'landing.erp.stats.modules' },
  { value: '16', label: 'landing.erp.stats.years' },
  { value: '+35k', label: 'landing.erp.stats.users' },
]

const aboutContent = [
  'landing.erp.about.items.integrated',
  'landing.erp.about.items.modern',
  'landing.erp.about.items.scalable',
]

const moduleContent = [
  { image: warehouseImage, key: 'inv' },
  { image: salesImage, key: 'sales' },
  { image: procurementImage, key: 'purchase' },
  { image: financeImage, key: 'finance' },
  { image: crmImage, key: 'crm' },
  { image: qualityImage, key: 'qm' },
]

const timelineContent = [
  { year: '2008', key: 'start' },
  { year: '2013', key: 'expand' },
  { year: '2019', key: 'ai' },
  { year: '2024', key: 'cloud' },
]

const aiContent = ['predict', 'quality', 'risk']
const journeyContent = ['step1', 'step2', 'step3', 'step4']

const activeCopy = computed(() => ({
  nav: [
    t('landing.footer.quick.about'),
    t('landing.erp.modules.title'),
    t('landing.erp.ai.title'),
    t('landing.footer.quick.contact'),
  ],
  auth: { login: t('login'), register: t('signup') },
  hero: {
    eyebrow: t('landing.hero.badge'),
    title: t('landing.erp.hero.title'),
    text: t('landing.erp.hero.subtitle'),
    primary: t('landing.erp.hero.contact'),
    secondary: t('landing.erp.hero.demo'),
  },
  stats: statsContent.map((stat) => ({ value: stat.value, label: t(stat.label) })),
  about: {
    eyebrow: t('landing.banner.text'),
    title: t('landing.erp.about.title'),
    text: t('landing.erp.about.description'),
    items: aboutContent.map((key) => t(key)),
  },
  modules: {
    title: t('landing.erp.modules.title'),
    text: t('landing.services.subtitle'),
  },
  timeline: {
    title: t('landing.erp.timeline.title'),
  },
  ai: {
    title: t('landing.erp.ai.title'),
    text: t('landing.ai.subtitle'),
  },
  journey: {
    title: t('landing.erp.journey.title'),
    text: t('landing.journey.subtitle'),
  },
  contact: {
    title: t('landing.contact.title'),
    text: t('landing.contact.subtitle'),
    phoneLabel: t('landing.contact.phoneTitle'),
    phone: t('landing.contact.phone'),
    emailLabel: t('landing.contact.emailTitle'),
    email: t('landing.contact.email'),
    hoursLabel: t('landing.contact.hoursTitle'),
    hours: t('landing.contact.hours'),
    proof: `${t('landing.contact.socialPrefix')} 500+ ${t('landing.contact.socialSuffix')}`,
    button: t('landing.erp.contact.action'),
  },
  footer: {
    tagline: t('landing.footer.tagline'),
    links: [
      t('landing.footer.quick.about'),
      t('landing.footer.quick.services'),
      t('landing.erp.ai.title'),
      t('landing.footer.quick.contact'),
    ],
    rights: t('landing.erp.footer.rights'),
  },
}))

const activeModules = computed(() => moduleContent.map((module) => ({
  image: module.image,
  title: t(`landing.erp.modules.${module.key}.title`),
  description: t(`landing.erp.modules.${module.key}.description`),
})))
const activeTimeline = computed(() => timelineContent.map((item) => ({
  year: item.year,
  text: t(`landing.erp.timeline.${item.key}`),
})))
const activeAi = computed(() => aiContent.map((key) => ({
  title: t(`landing.erp.ai.${key}.title`),
  description: t(`landing.erp.ai.${key}.description`),
})))
const activeJourney = computed(() => journeyContent.map((key) => t(`landing.erp.journey.${key}`)))
const chartBars = [36, 58, 46, 75, 55, 84]

const formatNumber = (value) => {
  if (selectedLanguage.value === 'en') return String(value).padStart(2, '0')

  const digits = selectedLanguage.value === 'ar' ? '٠١٢٣٤٥٦٧٨٩' : '۰۱۲۳۴۵۶۷۸۹'
  return String(value)
    .padStart(2, '0')
    .split('')
    .map((digit) => digits[Number(digit)] || digit)
    .join('')
}

const scrollToSection = (id) => {
  const target = document.getElementById(id)
  if (!target) return

  const top = target.getBoundingClientRect().top + window.scrollY - 24
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

const submitForm = async () => {
  if (isSubmitting.value) return

  isSubmitting.value = true
  formStatus.value = { type: '', message: '' }

  try {
    await contactService.submit({
      name: form.value.name.trim(),
      phone: form.value.phone.trim(),
      email: form.value.email.trim(),
      business_type: form.value.businessType,
      message: form.value.message.trim(),
    })

    form.value = {
      name: '',
      phone: '',
      email: '',
      businessType: '',
      message: '',
    }
    formStatus.value = {
      type: 'success',
      message: t('landing.contact.form.success', 'درخواست شما با موفقیت ارسال شد.'),
    }
  } catch (error) {
    console.error('Contact form submission failed:', error)
    formStatus.value = {
      type: 'error',
      message: error?.response?.data?.detail
        || t('landing.contact.form.error', 'ارسال درخواست انجام نشد. لطفاً دوباره تلاش کنید.'),
    }
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  const savedTheme = window.localStorage.getItem('landing-theme')
  const savedLanguage = window.localStorage.getItem('landing-language')

  if (savedTheme) isDark.value = savedTheme === 'dark'
  if (savedLanguage && languages.some((language) => language.value === savedLanguage)) {
    selectedLanguage.value = savedLanguage
  }
  locale.value = selectedLanguage.value
})

watch(isDark, (value) => {
  window.localStorage.setItem('landing-theme', value ? 'dark' : 'light')
})

watch(selectedLanguage, (value) => {
  locale.value = value
  window.localStorage.setItem('landing-language', value)
  window.localStorage.setItem('lang', value)
})
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap');

.landing-erp {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  isolation: isolate;
  direction: rtl;
  color: var(--text-primary);
  background: var(--page-background);
  background-color: var(--background-fallback);
  font-family: 'Vazirmatn', Tahoma, 'Segoe UI', sans-serif;
  text-align: right;
}

.landing-erp.ltr-mode {
  direction: ltr;
  text-align: left;
}

.landing-erp,
.landing-erp * {
  box-sizing: border-box;
}

.landing-erp::before,
.landing-erp::after {
  position: absolute;
  inset: 0;
  z-index: -3;
  content: '';
  pointer-events: none;
}

.landing-erp::before {
  background:
    radial-gradient(circle at 15% 18%, var(--landing-hero-glow-one), var(--landing-transparent) 27%),
    radial-gradient(circle at 84% 78%, var(--landing-hero-glow-two), var(--landing-transparent) 34%),
    linear-gradient(125deg, var(--landing-hero-wash-start), var(--landing-transparent) 38%, var(--landing-hero-wash-end) 100%);
}

.landing-erp::after {
  top: 80px;
  right: 50%;
  left: auto;
  width: 760px;
  height: 760px;
  border-radius: 50%;
  transform: translateX(50%);
  background: radial-gradient(circle, var(--landing-center-glow-one), var(--landing-center-glow-two) 38%, var(--landing-transparent) 70%);
}

.landing-erp a {
  color: inherit;
  text-decoration: none;
}

.landing-erp button,
.landing-erp a {
  -webkit-tap-highlight-color: var(--landing-transparent);
}

.landing-erp button {
  font: inherit;
}

.landing-container {
  position: relative;
  z-index: 2;
  width: min(1120px, calc(100% - 44px));
  margin-inline: auto;
}

.ambient {
  position: absolute;
  z-index: -2;
  border-radius: 50%;
  pointer-events: none;
}

.ambient-one {
  top: -250px;
  right: -180px;
  width: 530px;
  height: 530px;
  background: radial-gradient(circle, var(--landing-ambient-one), var(--landing-transparent) 70%);
}

.ambient-two {
  top: 440px;
  left: -230px;
  width: 480px;
  height: 480px;
  background: radial-gradient(circle, var(--landing-ambient-two), var(--landing-transparent) 70%);
}

.ambient-three {
  top: 1450px;
  right: 20%;
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, var(--landing-ambient-three), var(--landing-transparent) 70%);
}

.auth-buttons {
  display: contents;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: min(260px, 100%);
  margin-top: 12px;
}

.auth-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.auth-btn:hover {
  transform: translateY(-2px);
}

.auth-btn-login {
  border: 1px solid var(--ghost-border);
  color: var(--soft-text);
  background: var(--control-background-muted);
}

.auth-btn-login:hover {
  border-color: var(--accent);
}

.auth-btn-register {
  color: var(--landing-on-accent);
  background: var(--accent);
  box-shadow: 0 8px 20px var(--landing-accent-shadow);
}

.landing-nav {
  position: absolute;
  top: 14px;
  left: 50%;
  right: auto;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: nowrap;
  width: max-content;
  max-width: calc(100% - 32px);
  height: 62px;
  min-width: 0;
  justify-content: center;
  padding: 7px 10px 7px 22px;
  border: 1px solid var(--glass-border);
  border-radius: 21px;
  background: var(--nav-background);
  box-shadow: 0 12px 35px var(--glass-shadow);
  transform: translateX(-50%);
}

.nav-brand {
  display: inline-flex;
  width: 66px;
  height: 46px;
  flex: 0 0 66px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: var(--landing-transparent);
  transition: transform 0.2s ease;
}

.nav-brand img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.nav-brand:hover {
  transform: translateY(-2px);
}

.brand,
.footer-brand {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  flex: 0 0 auto;
  color: var(--brand-text);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.footer-logo {
  display: block;
  width: 82px;
  height: 52px;
  object-fit: contain;
  background: var(--landing-transparent);
}

.brand b,
.footer-brand b {
  color: var(--accent);
}

.brand-mark {
  display: inline-flex;
  align-items: flex-end;
  gap: 3px;
  height: 20px;
}

.brand-mark i {
  display: block;
  width: 4px;
  height: 13px;
  border-radius: 4px;
  background: var(--brand-mark-one);
}

.brand-mark i:nth-child(2) {
  height: 19px;
  background: var(--brand-mark-two);
}

.brand-mark i:nth-child(3) {
  height: 10px;
  background: var(--brand-mark-three);
}

.nav-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-inline: auto;
  flex: 0 0 auto;
  color: var(--nav-text);
  font-size: 13px;
  white-space: nowrap;
}

.nav-links a,
.footer-links a {
  transition: color 0.2s ease;
}

.nav-links a:hover,
.footer-links a:hover {
  color: var(--accent);
}

.nav-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  width: max-content;
}

.auth-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  width: max-content;
}

.auth-link {
  display: inline-flex;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: 1px solid var(--landing-transparent);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.auth-link:hover {
  transform: translateY(-2px);
}

.auth-login {
  border-color: var(--control-border);
  color: var(--text-primary);
  background: var(--control-background-muted);
}

.auth-login:hover {
  border-color: var(--accent);
  background: var(--interactive-hover);
}

.auth-register {
  color: var(--landing-on-accent);
  background: var(--accent);
  box-shadow: 0 8px 20px var(--landing-accent-shadow);
}

.language-picker {
  position: relative;
}

.language-trigger,
.theme-toggle {
  border: 1px solid var(--control-border);
  color: var(--text-primary);
  background: var(--control-background);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.language-trigger {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 116px;
  height: 46px;
  padding: 0 10px;
  border-radius: 13px;
  font-size: 12px;
}

.language-trigger:hover,
.language-picker.is-open .language-trigger,
.theme-toggle:hover {
  border-color: var(--accent);
  background: var(--interactive-hover);
}

.language-flag {
  display: grid;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  place-items: center;
  font-size: 16px;
  line-height: 1;
}

.language-name {
  flex: 1;
  text-align: start;
  white-space: nowrap;
}

.language-chevron {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  object-fit: contain;
  filter: var(--landing-language-chevron-filter);
  opacity: 0.8;
  transition: transform 0.2s ease;
}

.language-picker.is-open .language-chevron {
  transform: rotate(180deg);
}

.language-menu {
  position: absolute;
  top: calc(100% + 9px);
  right: 0;
  z-index: 20;
  display: grid;
  min-width: 180px;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--control-border);
  border-radius: 15px;
  background: var(--menu-background);
  box-shadow: 0 18px 40px var(--landing-menu-shadow);
}

.language-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 6px 9px;
  border: 0;
  border-radius: 10px;
  color: var(--text-primary);
  background: var(--landing-transparent);
  cursor: pointer;
  text-align: start;
}

.language-option:hover,
.language-option[aria-selected='true'] {
  color: var(--accent-soft);
  background: var(--interactive-hover);
}

.theme-toggle {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  padding: 0;
  border-radius: 12px;
  font-size: 19px;
}

.theme-toggle:hover {
  transform: translateY(-2px);
}

.hero {
  position: relative;
  display: grid;
  min-height: 760px;
  padding-top: 160px;
  padding-bottom: 105px;
  place-items: center;
  text-align: center;
}

.hero-copy-wrap {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(780px, 100%);
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.eyebrow {
  margin: 0 0 12px;
  color: var(--accent-soft);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.pulse-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 15px var(--landing-accent-glow-strong);
}

.hero h1,
.section h2,
.contact-section h2 {
  margin: 0;
  color: var(--heading-text);
  letter-spacing: -0.07em;
}

.hero h1 {
  max-width: 900px;
  margin: 20px 0 18px;
  font-size: clamp(46px, 7vw, 82px);
  line-height: 1.04;
  font-weight: 800;
  text-shadow: 0 8px 32px var(--heading-shadow);
}

.hero h1 em {
  color: var(--heading-emphasis);
  font-style: normal;
}

.hero-copy {
  max-width: 560px;
  margin: 0;
  color: var(--body-text);
  font-size: 20px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 30px;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 12px 18px;
  border: 1px solid var(--landing-transparent);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.button:hover {
  transform: translateY(-3px);
}

.button-primary {
  color: var(--landing-on-accent);
  background: var(--accent);
  box-shadow: 0 10px 28px var(--landing-accent-shadow);
}

.button-ghost {
  color: var(--soft-text);
  border-color: var(--ghost-border);
  background: var(--ghost-background);
}

.button-ghost span {
  margin-inline-start: 8px;
}

.hero-orbit {
  position: absolute;
  inset: 105px 0 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}

.hero-orbit::before {
  position: absolute;
  inset: 0;
  content: '';
  background: radial-gradient(ellipse at 50% 48%, var(--orbit-glow), var(--landing-transparent) 42%);
}

.orbit-ring {
  position: absolute;
  top: 48%;
  right: 50%;
  border: 1px solid var(--orbit-border);
  border-radius: 50%;
  transform: translate(50%, -50%) rotate(-12deg);
}

.ring-one {
  width: min(680px, 82%);
  height: 260px;
}

.ring-two {
  width: min(470px, 62%);
  height: 440px;
  border-color: var(--accent-border);
  transform: translate(50%, -50%) rotate(18deg);
}

.core {
  position: absolute;
  top: 48%;
  right: 50%;
  width: 330px;
  height: 330px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, var(--core-start), var(--core-mid) 60%, var(--landing-transparent) 72%);
  opacity: 0.8;
  transform: translate(50%, -50%);
}

.core span {
  position: absolute;
  inset: 23px;
  border: 1px solid var(--core-border);
  border-radius: 50%;
}

.orbit-dot {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 8px var(--landing-accent-shadow-soft), 0 0 16px var(--landing-accent-glow-strong);
}

.dot-a {
  top: 24%;
  right: 31%;
}

.dot-b {
  top: 49%;
  right: 13%;
}

.dot-c {
  top: 68%;
  right: 70%;
}

.orbit-label {
  position: absolute;
  color: var(--orbit-label);
  font-family: Tahoma, sans-serif;
  font-size: 10px;
  letter-spacing: 0.12em;
}

.label-a {
  top: 28%;
  right: 17%;
}

.label-b {
  top: 70%;
  right: 28%;
}

.metrics-strip {
  position: relative;
  z-index: 3;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.metric {
  display: flex;
  min-height: 128px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  border: 1px solid var(--card-border);
  border-radius: 18px;
  background: var(--card-background);
  box-shadow: 0 14px 30px var(--card-shadow-soft);
  text-align: center;
  transition: transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}

.metric:hover {
  border-color: var(--accent-border);
  background: var(--surface-hover);
  transform: translateY(-5px);
}

.metric strong {
  display: block;
  width: 100%;
  color: var(--metric-value);
  font-size: 38px;
  line-height: 1;
}

.metric span {
  display: block;
  width: 100%;
  margin-top: 12px;
  color: var(--metric-label);
  font-size: 13px;
}

.section {
  padding: 130px 0;
}

.section h2,
.contact-section h2 {
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.15;
}

.section-heading > p:not(.eyebrow),
.section-top > p,
.contact-copy > p:not(.eyebrow) {
  color: var(--section-copy);
  font-size: 16px;
  line-height: 1.9;
}

.section-heading > p:not(.eyebrow) {
  max-width: 540px;
  margin: 20px 0 0;
}

.section-heading ul {
  display: grid;
  gap: 14px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
}

.section-heading li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.8;
}

.check-mark {
  display: inline-grid;
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  place-items: center;
  border: 1px solid var(--accent-border);
  border-radius: 50%;
  color: var(--accent);
  font-size: 12px;
}

.centered {
  text-align: center;
}

.centered > p:not(.eyebrow) {
  margin-inline: auto;
}

.about {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.95fr);
  align-items: center;
  gap: 80px;
  direction: ltr;
}

.about-copy,
.about-visual {
  direction: rtl;
}

.ltr-mode .about-copy,
.ltr-mode .about-visual {
  direction: ltr;
}

.about-visual {
  position: relative;
  min-height: 350px;
  filter: drop-shadow(0 30px 45px var(--landing-floating-shadow));
}

.visual-grid {
  position: absolute;
  inset: 16px;
  border-radius: 28px;
  background:
    linear-gradient(var(--grid-line) 1px, var(--landing-transparent) 1px),
    linear-gradient(90deg, var(--grid-line) 1px, var(--landing-transparent) 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(135deg, var(--landing-mask-opaque), var(--landing-transparent) 80%);
  opacity: 0.8;
}

.dashboard-card {
  position: relative;
  z-index: 1;
  width: min(440px, 90%);
  min-height: 275px;
  margin: 34px auto 0;
  padding: 26px 28px;
  overflow: hidden;
  border: 1px solid var(--dashboard-border);
  border-radius: 20px;
  background: linear-gradient(135deg, var(--dashboard-start), var(--dashboard-end));
  box-shadow: 0 24px 60px var(--landing-dashboard-shadow);
  transform: rotate(-5deg);
}

.dashboard-card::before {
  position: absolute;
  inset: 0;
  content: '';
  background: radial-gradient(circle at 12% 12%, var(--dashboard-glow), var(--landing-transparent) 42%);
  pointer-events: none;
}

.mini-label {
  position: relative;
  color: var(--accent-soft);
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  letter-spacing: 0.08em;
}

.chart {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 155px;
  margin-top: 26px;
  padding: 10px 7px 0;
  border-bottom: 1px solid var(--chart-border);
}

.chart::before {
  position: absolute;
  inset: 0;
  content: '';
  background: repeating-linear-gradient(to bottom, var(--landing-transparent) 0 37px, var(--chart-grid) 38px 39px);
}

.chart i {
  position: relative;
  z-index: 1;
  display: block;
  flex: 1;
  min-width: 12px;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(var(--chart-start), var(--chart-end));
  box-shadow: 0 0 15px var(--chart-glow);
}

.dashboard-line {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 18px;
  color: var(--dashboard-text);
  font-size: 12px;
}

.dashboard-line b {
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
}

.dashboard-line strong {
  margin-inline-start: auto;
  color: var(--accent-soft);
  font-size: 13px;
}

.section-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 45px;
  margin-bottom: 36px;
}

.section-top > p {
  max-width: 370px;
  margin: 0;
}

.section-top-centered {
  display: block;
  text-align: center;
}

.section-top-centered > div {
  display: grid;
  justify-items: center;
}

.section-subtitle {
  max-width: 540px;
  margin: 18px 0 0;
  color: var(--section-copy);
  font-size: 18px;
  line-height: 1.9;
}

.centered .section-subtitle {
  margin-inline: auto;
}

.module-grid,
.ai-grid,
.journey-grid {
  display: grid;
  gap: 20px;
}

.module-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.module-card {
  position: relative;
  display: flex;
  min-height: 260px;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid var(--border);
  border-left: 3px solid var(--accent);
  border-radius: 18px;
  background: var(--surface-strong);
  box-shadow: 0 14px 30px var(--card-shadow);
  transition: transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}

.module-card::after {
  position: absolute;
  inset: 0;
  z-index: 1;
  content: '';
  background: linear-gradient(180deg, var(--module-overlay-start) 18%, var(--module-overlay-end) 100%);
  pointer-events: none;
}

.module-card:hover {
  border-color: var(--accent-border);
  transform: translateY(-5px);
}

.module-card-image {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.4s ease;
}

.module-card:hover .module-card-image {
  transform: scale(1.05);
}

.module-card-content {
  position: relative;
  z-index: 2;
  padding: 24px 24px 22px;
}

.module-number {
  display: block;
  margin-bottom: 10px;
  color: var(--landing-transparent);
  font-family: Tahoma, sans-serif;
  font-size: 13px;
  font-weight: 700;
}

.module-card h3,
.ai-card h3,
.journey-step h3 {
  margin: 0 0 10px;
  color: var(--heading-text);
  font-size: 20px;
  line-height: 1.45;
}

.module-card p,
.ai-card p,
.journey-step p,
.erp-timeline-item p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.85;
}

.timeline-section {
  padding-bottom: 135px;
}

.erp-timeline {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 62px;
  border-top: 1px solid var(--timeline-border);
}

.erp-timeline-item {
  position: relative;
  min-height: 132px;
  padding: 26px 22px 0;
}

.erp-timeline-item:not(:last-child) {
  border-left: 0 !important;
  border-right: 0 !important;
  box-shadow: none !important;
}

/* Disable legacy/global pseudo-elements so the timeline only keeps its intended horizontal track. */
.erp-timeline-item::before,
.erp-timeline-item::after {
  display: none !important;
  content: none !important;
}

.erp-timeline-dot {
  position: absolute;
  top: -7px;
  right: 22px;
  display: grid;
  width: 13px;
  height: 13px;
  place-items: center;
  border: 3px solid var(--background);
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 17px var(--landing-accent-glow-strong);
  color: var(--landing-transparent);
}

.erp-timeline-item strong {
  display: block;
  margin-bottom: 14px;
  color: var(--accent);
  font-family: Tahoma, sans-serif;
  font-size: 19px;
}

.ai-section {
  background: linear-gradient(180deg, var(--landing-transparent), var(--section-glow), var(--landing-transparent));
  border-block: 1px solid var(--section-border);
}

.footer-zone {
  position: relative;
  z-index: 1;
  background: linear-gradient(180deg, var(--landing-footer-start), var(--landing-footer-end));
}

/* Keep global application styles from turning the landing contact/footer area white. */
.footer-zone .contact-section,
.footer-zone .footer {
  background: var(--landing-transparent) !important;
  box-shadow: none !important;
}

.ai-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ai-card,
.journey-step,
.social-proof {
  border: 1px solid var(--border);
  border-radius: 18px;
  background: var(--card-background);
  box-shadow: 0 14px 30px var(--card-shadow);
  transition: transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}

.ai-card {
  min-height: 170px;
  padding: 30px 26px;
  border-left: 3px solid var(--accent);
}

.ai-card:hover,
.journey-step:hover {
  border-color: var(--accent-border);
  background: var(--surface-hover);
  transform: translateY(-5px);
}

.journey-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.journey-step {
  display: grid;
  min-height: 170px;
  place-items: center;
  padding: 30px 24px;
  border-left: 3px solid var(--accent);
  text-align: center;
}

.journey-step h3 {
  font-size: 18px;
}

.contact-section {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  align-items: start;
  column-gap: 72px;
  row-gap: 36px;
  padding-top: 125px;
  padding-bottom: 115px;
  direction: ltr;
}

.contact-copy,
.social-proof {
  direction: rtl;
}

.ltr-mode .contact-copy,
.ltr-mode .social-proof {
  direction: ltr;
}

.ltr-mode .contact-form-card {
  direction: ltr;
}

.contact-copy h2 {
  max-width: 420px;
}

.contact-copy > p:not(.eyebrow) {
  max-width: 430px;
  margin: 20px 0 0;
}

.contact-form-card {
  display: contents;
  direction: rtl;
}

.contact-form {
  grid-column: 2;
  align-self: start;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 24px;
  border: 1px solid var(--border);
  border-radius: 18px;
  background: var(--card-background);
  box-shadow: 0 14px 30px var(--card-shadow);
  direction: rtl;
}

.ltr-mode .contact-form {
  direction: ltr;
}

.form-field {
  display: grid;
  gap: 7px;
  color: var(--text-muted);
  font-size: 12px;
}

.form-field-wide,
.form-submit,
.form-status {
  grid-column: 1 / -1;
}

.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid var(--control-border);
  border-radius: 10px;
  color: var(--text-primary);
  background: var(--control-background);
  font: inherit;
}

.form-field textarea {
  min-height: 105px;
  resize: vertical;
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--ring);
}

.form-field select option {
  color: var(--text-primary);
  background: var(--menu-background);
}

.form-submit {
  width: 100%;
}

.form-submit:disabled {
  cursor: wait;
  opacity: 0.72;
}

.form-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-inline-end: 7px;
  border: 2px solid var(--landing-spinner-track);
  border-top-color: var(--landing-on-accent);
  border-radius: 50%;
  vertical-align: -2px;
  animation: contact-spin 0.7s linear infinite;
}

.form-status {
  margin: 0;
  font-size: 12px;
  text-align: center;
}

.form-status-success {
  color: var(--landing-status-success);
}

.form-status-error {
  color: var(--landing-status-error);
}

@keyframes contact-spin {
  to {
    transform: rotate(360deg);
  }
}

.contact-details {
  display: grid;
  gap: 15px;
  margin-top: 30px;
}

.contact-details div {
  display: flex;
  align-items: baseline;
  gap: 15px;
  color: var(--contact-text);
  font-size: 13px;
}

.contact-details span {
  min-width: 75px;
  color: var(--text-muted);
  font-size: 12px;
}

.contact-details a,
.contact-details b {
  color: var(--contact-text);
  font-weight: 500;
}

.contact-links {
  display: grid;
  gap: 18px;
  margin-top: 32px;
}

.contact-social-links h3 {
  margin: 0 0 10px;
  color: var(--heading-text);
  font-size: 15px;
}

.social-icons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--control-border);
  color: var(--contact-text);
  background: var(--control-background-muted);
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.social-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 17px;
}

.social-icon img {
  width: 19px;
  height: 19px;
  object-fit: contain;
  filter: var(--landing-social-icon-filter);
}

.social-icon:hover {
  border-color: var(--accent);
  background: var(--interactive-hover);
  transform: translateY(-2px);
}

.social-proof {
  grid-column: 1 / -1;
  width: 100%;
  display: flex;
  min-height: 170px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 30px;
  border-color: var(--accent-border);
  background: radial-gradient(circle at 50% 0, var(--chart-glow), var(--landing-transparent) 60%), var(--card-background);
  text-align: center;
}

.proof-orb {
  display: grid;
  width: 44px;
  height: 44px;
  margin-bottom: 20px;
  place-items: center;
  border-radius: 50%;
  color: var(--landing-proof-orb-text);
  background: var(--landing-proof-orb-surface);
  box-shadow: 0 0 35px var(--landing-accent-shadow);
}

.social-proof h3 {
  margin: 0 0 24px;
  color: var(--heading-text);
  font-size: 20px;
}

.footer {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 28px;
  min-height: 112px;
  padding-top: 25px;
  padding-bottom: 25px;
  border-top: 1px solid var(--footer-border);
  color: var(--text-secondary);
  font-size: 13px;
}

.footer > p {
  margin: 0;
  text-align: center;
}

.footer small {
  grid-column: 1 / -1;
  color: var(--text-muted);
  font-size: 11px;
  text-align: center;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  white-space: nowrap;
}

.footer-links a {
  color: var(--text-secondary);
}

.light-mode {
  background: var(--page-background);
  background-color: var(--background-fallback);
}
.light-mode .ai-card:hover {
  box-shadow:
    0 14px 30px var(--card-shadow),
    0 0 18px var(--landing-accent-shadow-soft);
}

.light-mode .hero-orbit {
  opacity: 0.58;
}

@media (max-width: 900px) {
  .landing-container {
    width: min(100% - 32px, 1120px);
  }

  .landing-nav {
    width: max-content;
    max-width: calc(100% - 24px);
  }

  .nav-links {
    gap: 15px;
    font-size: 11px;
  }

  .hero {
    min-height: 680px;
    padding-top: 145px;
  }

  .about,
  .contact-section {
    grid-template-columns: 1fr;
    gap: 55px;
  }

  .about-copy,
  .about-visual,
  .contact-copy,
  .social-proof {
    grid-column: auto;
  }

  .contact-form {
    grid-column: auto;
  }

  .about-visual {
    min-height: 310px;
  }

  .module-grid,
  .ai-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .journey-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .erp-timeline {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    border-top: 0;
  }

  .erp-timeline-item {
    padding-top: 22px;
    border-top: 1px solid var(--accent-border);
  }

  .erp-timeline-item:not(:last-child) {
    border-left: 0;
  }

  .erp-timeline-dot {
    top: -7px;
  }
}

@media (max-width: 640px) {
  .auth-buttons {
    display: grid;
  }
  .landing-container {
    width: min(100% - 28px, 1120px);
  }

  .landing-nav {
    width: fit-content;
    max-width: calc(100% - 24px);
    height: 54px;
    gap: 8px;
    padding: 5px 7px 5px 10px;
  }

  .nav-brand {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
    padding: 2px;
    border-radius: 11px;
  }

  .brand {
    gap: 6px;
    font-size: 14px;
  }

  .nav-links {
    display: none;
  }

  .auth-actions {
    gap: 4px;
  }

  .auth-link {
    min-height: 34px;
    padding-inline: 8px;
    font-size: 12px;
  }

  .language-trigger {
    min-width: 42px;
    width: 42px;
    justify-content: center;
    padding: 0;
  }

  .language-name,
  .language-chevron {
    display: none;
  }

  .theme-toggle {
    width: 38px;
    height: 38px;
  }

  .hero {
    min-height: 610px;
    padding-top: 125px;
    padding-bottom: 76px;
  }

  .hero h1 {
    font-size: clamp(38px, 11vw, 58px);
  }

  .hero-copy {
    font-size: 16px;
  }

  .hero-actions {
    flex-direction: column;
    width: min(250px, 100%);
    margin-top: 25px;
  }

  .hero-actions .button {
    width: 100%;
  }

  .hero-orbit {
    top: 80px;
  }

  .ring-one {
    width: 98%;
  }

  .ring-two {
    width: 70%;
  }

  .core {
    width: 230px;
    height: 230px;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .metric {
    min-height: 105px;
    padding: 18px 10px;
  }

  .metric strong {
    font-size: 29px;
  }

  .metric span {
    margin-top: 9px;
    font-size: 14px;
  }

  .section {
    padding: 88px 0;
  }

  .section h2,
  .contact-section h2 {
    font-size: 32px;
  }

  .section-top {
    display: block;
  }

  .section-top > p {
    margin-top: 20px;
  }

  .module-grid,
  .ai-grid,
  .journey-grid {
    grid-template-columns: 1fr;
    gap: 36px;
  }

  .module-card {
    min-height: 240px;
  }

  .erp-timeline {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .erp-timeline-item {
    min-height: 115px;
  }

  .contact-details div {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .contact-details span {
    min-width: 0;
  }

  .footer {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
    padding-top: 32px;
    padding-bottom: 32px;
  }

  .footer > p {
    text-align: start;
  }

  .footer small {
    width: 100%;
    text-align: start;
  }
}

@media (max-width: 430px) {
  .landing-container {
    width: calc(100% - 24px);
  }

  .landing-nav {
    left: 50%;
    right: auto;
    width: fit-content;
    max-width: calc(100% - 16px);
    min-width: 0;
    height: 52px;
    gap: 5px;
    padding: 4px 5px;
    transform: translateX(-50%);
  }

  .nav-brand {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
    border-radius: 10px;
  }

  .nav-controls,
  .auth-actions {
    gap: 4px;
  }

  .auth-link {
    min-height: 34px;
    padding: 0 7px;
    border-radius: 9px;
    font-size: 14px;
  }

  .language-trigger {
    width: 36px;
    min-width: 36px;
    height: 36px;
  }

  .language-flag {
    width: 18px;
    height: 18px;
    flex-basis: 18px;
    font-size: 13px;
  }

  .theme-toggle {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 16px;
  }

  .hero {
    min-height: 600px;
    padding-top: 116px;
    padding-bottom: 64px;
  }

  .hero-copy-wrap {
    width: 100%;
  }

  .hero-eyebrow,
  .eyebrow {
    font-size: 14px;
  }

  .hero h1 {
    width: 100%;
    margin: 17px 0 15px;
    font-size: clamp(31px, 10.5vw, 45px);
    line-height: 1.12;
    letter-spacing: -0.055em;
  }

  .hero-copy {
    width: 100%;
    font-size: 16px;
    line-height: 1.85;
  }

  .hero-actions {
    width: min(260px, 100%);
  }

  .hero-orbit {
    inset: 74px 0 0;
  }

  .ring-one {
    width: 118%;
    height: 235px;
  }

  .ring-two {
    width: 78%;
    height: 380px;
  }

  .core {
    width: 205px;
    height: 205px;
  }

  .dot-a {
    right: 23%;
  }

  .dot-b {
    right: 5%;
  }

  .dot-c {
    right: 78%;
  }

  .orbit-label {
    display: none;
  }

  .metrics-grid {
    gap: 8px;
  }

  .metric {
    min-height: 96px;
    padding: 15px 7px;
    border-radius: 14px;
  }

  .metric strong {
    font-size: clamp(24px, 8vw, 30px);
  }

  .metric span {
    font-size: 13px;
    line-height: 1.5;
  }

  .section {
    padding: 72px 0;
  }

  .section h2,
  .contact-section h2 {
    font-size: clamp(27px, 9vw, 34px);
  }

  .section-heading > p:not(.eyebrow),
  .section-top > p,
  .contact-copy > p:not(.eyebrow) {
    font-size: 15px;
    line-height: 1.9;
  }

  .about-visual {
    min-height: 255px;
  }

  .dashboard-card {
    width: 94%;
    min-height: 225px;
    padding: 20px 18px;
  }

  .chart {
    height: 122px;
    gap: 7px;
    margin-top: 20px;
    padding-inline: 3px;
  }

  .chart i {
    min-width: 8px;
  }

  .dashboard-line {
    gap: 6px;
    margin-top: 13px;
    font-size: 13px;
  }

  .dashboard-line strong {
    font-size: 14px;
  }

  .module-card {
    min-height: 220px;
  }

  .module-card-content {
    padding: 20px 18px 18px;
  }

  .module-card h3,
  .ai-card h3,
  .journey-step h3 {
    font-size: 16px;
  }

  .module-card p,
  .ai-card p,
  .journey-step p,
  .erp-timeline-item p {
    font-size: 14px;
  }

  .erp-timeline-item {
    min-height: 100px;
    padding: 21px 15px 0;
  }

  .erp-timeline-item strong {
    font-size: 16px;
  }

  .erp-timeline-dot {
    right: 15px;
  }

  .journey-step,
  .ai-card {
    min-height: 145px;
    padding: 22px 18px;
  }

  .contact-section {
    gap: 35px;
    padding-top: 75px;
    padding-bottom: 75px;
  }

  .contact-form {
    grid-template-columns: 1fr;
    padding: 18px;
  }

  .form-field-wide,
  .form-submit,
  .form-status {
    grid-column: auto;
  }

  .social-proof {
    min-height: 200px;
    padding: 30px 18px;
  }

  .social-proof h3 {
    font-size: 17px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-erp *,
  .landing-erp *::before,
  .landing-erp *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
